import {
  BookingRequestsStatus,
  type PatchActiveBookingRequestsDto
} from "../../../components/BookingRequest/typings/booking-requests.types";
import type { LocationInventory } from "../../FloorManager/typings/location-inventory";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { resolveLocationInventoryIdForPatch } from "./functions/buildPatchVisitOnlyBookingPayload.function";
import { isVisitorBookingAwaitingApproval } from "./visitor-bookings-registration-actions.logic";
import {
  isConferenceVisitorRegistrationRow,
  type VisitorBookingTableRow
} from "./visitor-bookings-table.partial";

function locationIdFromBookingInventory(visit: BookingScheduleInterface): number | null {
  const inventory = visit.bookingInventory;
  if (inventory == null) {
    return null;
  }
  if (
    "locationInventoryId" in inventory &&
    inventory.locationInventoryId != null &&
    inventory.locationInventoryId > 0
  ) {
    return inventory.locationInventoryId;
  }
  const nestedLocationId = (inventory as { locationInventory?: { id?: number } }).locationInventory
    ?.id;
  if (nestedLocationId != null && nestedLocationId > 0) {
    return nestedLocationId;
  }
  return null;
}

/** Conference visitor admin rows often omit top-level `locationInventoryId`; resolve from inventory or name. */
export function resolveVisitorBookingLocationInventoryId(
  visit: BookingScheduleInterface,
  locations?: LocationInventory[]
): number | null {
  if (visit.locationInventoryId != null && visit.locationInventoryId > 0) {
    return visit.locationInventoryId;
  }
  const fromInventory = locationIdFromBookingInventory(visit);
  if (fromInventory != null) {
    return fromInventory;
  }
  const fromName = resolveLocationInventoryIdForPatch(locations, visit.locationName);
  return fromName ?? null;
}

/** True when the user is request admin for the visit's location (independent of booking status). */
export function canManageRequestedVisitorBooking(
  visit: BookingScheduleInterface,
  canManageAtLocation: (locationInventoryId: number | null | undefined) => boolean,
  locations?: LocationInventory[]
): boolean {
  const locationId = resolveVisitorBookingLocationInventoryId(visit, locations);
  return canManageAtLocation(locationId);
}

export function resolveBookingApprovalRejectEntry(
  visit: BookingScheduleInterface
): { bookingId: number; bookingType: BookingType } | null {
  if (!isVisitorBookingAwaitingApproval(visit)) {
    return null;
  }

  if (isConferenceVisitorRegistrationRow(visit)) {
    const bookingId = visit.noPlaceZoneBookingId;
    if (bookingId == null || bookingId <= 0) {
      return null;
    }
    return { bookingId, bookingType: BookingType.CONFERENCEZONE };
  }

  const bookingId = visit.id;
  const bookingType = visit.bookingType as BookingType;
  if (!Number.isFinite(bookingId) || bookingId <= 0 || bookingType == null) {
    return null;
  }
  return { bookingId, bookingType };
}

export function buildBookingApprovalRejectPayloadForVisit(
  visit: BookingScheduleInterface,
  reason?: string
): PatchActiveBookingRequestsDto | null {
  return buildBookingApprovalRejectPayload([visit], reason);
}

export function buildBookingApprovalRejectPayload(
  rows: readonly BookingScheduleInterface[],
  reason?: string
): PatchActiveBookingRequestsDto | null {
  const bookingData: Array<{ bookingId: number; bookingType: BookingType }> = [];
  const seen = new Set<string>();

  for (const row of rows) {
    const entry = resolveBookingApprovalRejectEntry(row);
    if (!entry) {
      continue;
    }
    const key = `${entry.bookingType}:${entry.bookingId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    bookingData.push(entry);
  }

  if (bookingData.length === 0) {
    return null;
  }

  const trimmedReason = reason?.trim();
  return {
    status: BookingRequestsStatus.REJECTED,
    bookingData,
    ...(trimmedReason != null && trimmedReason.length > 0 ? { rejectionReason: trimmedReason } : {})
  };
}

export function partitionRowsForVisitorReject(
  rows: VisitorBookingTableRow[],
  canManageRequested: (visit: BookingScheduleInterface) => boolean
): {
  approvalRejectRows: VisitorBookingTableRow[];
  visitorAdminRejectRows: VisitorBookingTableRow[];
} {
  const approvalRejectRows: VisitorBookingTableRow[] = [];
  const visitorAdminRejectRows: VisitorBookingTableRow[] = [];

  for (const row of rows) {
    if (isVisitorBookingAwaitingApproval(row) && canManageRequested(row)) {
      approvalRejectRows.push(row);
    } else {
      visitorAdminRejectRows.push(row);
    }
  }

  return { approvalRejectRows, visitorAdminRejectRows };
}
